/**
 * DataSourceList Component (TASK-017)
 *
 * Lists data sources with filtering, status badges, and actions.
 */
import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Trash2, Loader2, AlertCircle, Database, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import apiClient from '@/services/api/client';
import { dataSourcesApi } from '@/services/api/dataSources';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { PaginationBar } from '@/components/common/PaginationBar';
import { usePageSize } from '@/hooks/usePageSize';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { DataSource, DataSourceFilters, DataSourceStatus } from '@/types/dataSource';

interface CurrentUser {
  id: string;
  permissions?: {
    data_sources?: {
      view?: boolean;
      create?: boolean;
      update?: boolean;
      delete?: boolean;
    };
  };
}

interface DataSourceListProps {
  onSuccess?: (message: string) => void;
}

const STATUS_DOT_CLASSES: Record<DataSourceStatus, string> = {
  PENDING: 'bg-yellow-400',
  PROCESSING: 'bg-blue-500',
  COMPLETED: 'bg-green-500',
  FAILED: 'bg-red-500',
};

const STATUS_TEXT_CLASSES: Record<DataSourceStatus, string> = {
  PENDING: 'text-yellow-700',
  PROCESSING: 'text-blue-700',
  COMPLETED: 'text-green-700',
  FAILED: 'text-red-700',
};

const TYPE_BADGE_CLASSES: Record<string, string> = {
  EXCEL: 'bg-green-50 text-green-700 border border-green-200',
  WEB: 'bg-blue-50 text-blue-700 border border-blue-200',
  FEED: 'bg-blue-50 text-blue-700 border border-blue-200',
  WIKI: 'bg-gray-100 text-gray-600 border border-gray-200',
};

const STATUS_LABELS: Record<DataSourceStatus, string> = {
  PENDING: 'Ожидание',
  PROCESSING: 'Обработка',
  COMPLETED: 'Завершено',
  FAILED: 'Ошибка',
};

export function DataSourceList({ onSuccess }: DataSourceListProps): JSX.Element {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<DataSourceFilters>({});
  const { page, setPage, pageSize, setPageSize } = usePageSize('data-sources');

  // Fetch current user permissions (shared cache key with Sidebar and detail page).
  const { data: currentUser } = useQuery<CurrentUser>({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const res = await apiClient.get<CurrentUser>('/api/v1/auth/me/');
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const canDelete = currentUser?.permissions?.data_sources?.delete === true;
  const [searchValue, setSearchValue] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<DataSource | null>(null);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: searchValue || undefined,
      }));
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue, setPage]);

  // Query for listing data sources
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['dataSources', filters, page, pageSize],
    queryFn: () => dataSourcesApi.list({ ...filters, page, page_size: pageSize }),
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => dataSourcesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dataSources'] });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      onSuccess?.('Источник данных удалён');
    },
  });

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value);
    },
    []
  );

  const handleTypeChange = useCallback((value: string) => {
    setFilters((prev) => ({
      ...prev,
      type: value === 'all' ? undefined : value,
    }));
    setPage(1);
  }, [setPage]);

  // NOTE: status filter Select is intentionally commented out (см. JSX ниже),
  // поэтому handleStatusChange удалён как мёртвый код — иначе tsc (noUnusedLocals)
  // валит прод-сборку (TS6133). Вернуть вместе с раскомментированием фильтра.


  const handleDeleteClick = useCallback((item: DataSource) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (itemToDelete) {
      deleteMutation.mutate(itemToDelete.id);
    }
  }, [itemToDelete, deleteMutation]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div
        data-testid="data-source-list-loading"
        className="flex items-center justify-center py-12"
      >
        <div role="status" className="flex items-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-2 text-gray-600">Загрузка...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div
        data-testid="data-source-list-error"
        className="flex flex-col items-center justify-center py-12"
      >
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <p className="text-gray-600 mb-4">
          Ошибка загрузки источников: {error?.message || 'Неизвестная ошибка'}
        </p>
        <Button
          data-testid="retry-button"
          variant="outline"
          onClick={() => refetch()}
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Повторить
        </Button>
      </div>
    );
  }

  // Empty state
  if (!data?.results?.length) {
    return (
      <div>
        {/* Filters */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              data-testid="search-input"
              placeholder="Поиск источников..."
              value={searchValue}
              onChange={handleSearchChange}
              className="w-full pl-9"
            />
          </div>
          <Select value={filters.type || 'all'} onValueChange={handleTypeChange}>
            <SelectTrigger data-testid="type-filter" className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все типы</SelectItem>
              <SelectItem value="EXCEL">Excel файл</SelectItem>
              <SelectItem value="WEB">Web URL</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div
          data-testid="data-source-list-empty"
          className="flex flex-col items-center justify-center py-12 border rounded-lg bg-gray-50"
        >
          <Database className="h-12 w-12 text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-600 mb-2">Нет источников данных</p>
          <p className="text-sm text-gray-500">Добавьте первый источник данных для начала работы.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <Input
            data-testid="search-input"
            placeholder="Поиск источников..."
            value={searchValue}
            onChange={handleSearchChange}
            className="w-full pl-9"
          />
        </div>
        <Select value={filters.type || 'all'} onValueChange={handleTypeChange}>
          <SelectTrigger data-testid="type-filter" className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все типы</SelectItem>
            <SelectItem value="EXCEL">Excel файл</SelectItem>
            <SelectItem value="WEB">Web URL</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table (desktop) */}
      <div className="hidden md:block bg-white border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Название</TableHead>
              <TableHead>Тип</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead className="text-right">Символов</TableHead>
              <TableHead>Обновлён</TableHead>
              <TableHead className="text-right">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.results.map((source) => (
              <TableRow
                key={source.id}
                data-testid={`data-source-row-${source.id}`}
              >
                <TableCell className="font-medium">
                  <div>
                    <Link
                      to={`/data-sources/${source.id}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {source.name}
                    </Link>
                    {(source.url || source.file) && (
                      <div className="text-xs text-slate-400 mt-0.5 truncate max-w-xs">
                        {source.url
                          ? source.url.replace(/^https?:\/\//, '')
                          : source.file?.split('/').pop()}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <span
                    data-testid={`type-badge-${source.id}`}
                    className={cn(
                      'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                      TYPE_BADGE_CLASSES[source.type] ?? 'bg-gray-100 text-gray-600 border border-gray-200'
                    )}
                  >
                    {source.type_display}
                  </span>
                </TableCell>
                <TableCell>
                  <span
                    data-testid={`status-badge-${source.id}`}
                    className={cn('inline-flex items-center gap-1.5 text-xs font-medium', STATUS_TEXT_CLASSES[source.status])}
                  >
                    <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', STATUS_DOT_CLASSES[source.status])} />
                    {STATUS_LABELS[source.status]}
                  </span>
                </TableCell>
                <TableCell className="text-right text-sm text-slate-400">
                  {source.characters_count != null
                    ? source.characters_count >= 1000
                      ? `${(source.characters_count / 1000).toFixed(1)}k`
                      : String(source.characters_count)
                    : '—'}
                </TableCell>
                <TableCell className="text-sm text-slate-400">
                  {source.indexed_at
                    ? new Date(source.indexed_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
                    : '—'}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {canDelete && (
                      <Button
                        data-testid={`delete-button-${source.id}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(source)}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Cards (mobile) */}
      <div className="space-y-3 md:hidden">
        {data.results.map((source) => (
          <div
            key={source.id}
            data-testid={`data-source-card-${source.id}`}
            className="bg-white border rounded-lg p-4 space-y-3"
          >
            <Link
              to={`/data-sources/${source.id}`}
              className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
            >
              {source.name}
            </Link>
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <span
                  data-testid={`type-badge-${source.id}`}
                  className={cn(
                    'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                    TYPE_BADGE_CLASSES[source.type] ?? 'bg-gray-100 text-gray-600 border border-gray-200'
                  )}
                >
                  {source.type_display}
                </span>
                <span
                  data-testid={`status-badge-${source.id}`}
                  className={cn('inline-flex items-center gap-1.5 text-xs font-medium', STATUS_TEXT_CLASSES[source.status])}
                >
                  <span className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0', STATUS_DOT_CLASSES[source.status])} />
                  {STATUS_LABELS[source.status]}
                </span>
              </div>
              <div className="flex gap-1">
                {canDelete && (
                  <Button
                    data-testid={`delete-button-${source.id}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteClick(source)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <PaginationBar
        page={page}
        pageSize={pageSize}
        count={data.count}
        hasPrevious={Boolean(data.previous)}
        hasNext={Boolean(data.next)}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        className="px-0"
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle>Удаление источника</DialogTitle>
            <DialogDescription>
              Вы уверены, что хотите удалить &quot;{itemToDelete?.name}&quot;?
              Это действие нельзя отменить.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              data-testid="cancel-delete-button"
              variant="outline"
              onClick={handleDeleteCancel}
            >
              Отмена
            </Button>
            <Button
              data-testid="confirm-delete-button"
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Удаление...
                </>
              ) : (
                'Удалить'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DataSourceList;
